import { PromptConfirm } from '../..'
import { _ } from '../../../lib'
import { svrtsRequest } from '../../svrts-interface'
import { getFlattenedTags } from './tags'
import { NoteInfo, ReducerState } from './types'

export const mergeDocuments = <T extends Record<string, unknown>>(
	rs: ReducerState<T>,
	selected: NoteInfo<T>[],
) => {
	// Ask the user for consent to merge
	PromptConfirm({
		msg: 'Are you sure you want to merge these documents?',
		yes: callback => {
			actuallyRunMerge(rs, selected, callback)
		},
	})
}

const actuallyRunMerge = <T extends Record<string, unknown>>(
	rs: ReducerState<T>,
	selected: NoteInfo<T>[],
	callback: (trueOrError: string | true) => void,
) => {
	svrtsRequest({
		data: {
			funcID: [0, 64],
			checksumsToMerge: _.chain(selected)
				.map(x => x.Attachment?.Checksum)
				.compact()
				.uniq()
				.value(),
		},
		no: data => {
			callback(data.message)
		},
		yes: data => {
			createMergedNote(rs, selected, data.newChecksum, callback)
		},
	})
}

const createMergedNote = <T extends Record<string, unknown>>(
	rs: ReducerState<T>,
	selected: NoteInfo<T>[],
	newChecksum: string,
	callback: (trueOrError: string | true) => void,
) => {
	// Create the new note
	const titles = _.chain(selected)
		.map(x => x.Title)
		.compact()
		.uniq()
		.sort()
		.map(x => `- ${x}`)
		.value()
	const notes = _.chain(selected)
		.map(x => x.Note)
		.compact()
		.uniq()
		.sort()
		.value()
	const newNoteParts = [
		titles.length > 0 ? `Old titles:\n\n${titles.join('\n')}\n\n` : '',
		notes.length > 0 ? `Old notes:\n\n${notes.join('\n\n-----\n\n')}\n\n` : '',
	]

	// Identify the editable tags
	const editableTags = _.chain(getFlattenedTags(rs))
		.filter(x => !(x.NonEditable ?? false))
		.map(x => x.ID)
		.uniq()
		.value()

	// Call the handler to create a new note
	rs.props.adding?.onAdd({
		note: {
			Title: 'Merged Document',
			Note: _.compact(newNoteParts).join('\n'),
			Attachment: newChecksum,
			Tags: _.chain(selected)
				.flatMap(x => x.Tags)
				.filter(x => editableTags.includes(x))
				.uniq()
				.value(),
		},
		onComplete: (newID, successOrError) => {
			// Display any error
			if (_.isString(successOrError)) {
				callback(successOrError)
				return
			}

			// Delete the old notes
			deleteOldPreMergeNotes(rs, selected, newID, callback)
		},
	})
}

const deleteOldPreMergeNotes = <T extends Record<string, unknown>>(
	rs: ReducerState<T>,
	selected: NoteInfo<T>[],
	newNoteID: string,
	callback: (trueOrError: string | true) => void,
) => {
	// Delete the old notes
	rs.props.deleting?.onDelete(
		selected.map(x => x.ID),
		result => {
			// Select the new note
			if (newNoteID) {
				rs.updateState({
					selectedNotes: [newNoteID],
				})
			}

			// If it fails, we don't care
			// Optimally we should delete the note that was just added
			// But that seems annoying to manage for an event that shouldn't happen
			if (_.isString(result)) {
				console.error('Error deleting old notes after merging:', result)
			}
			callback(true)
		},
	)
}
